// Settings tab — the project's upload limits (size ceiling, allowed
// extensions), enforced by the upload path the moment they are saved. The
// default visibility of new files and the signing section moved to the
// Policies tab.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Save, Settings2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FieldError } from '@/shared/ui/FieldError.jsx';
import { fieldErrors } from '@/shared/utils/fieldErrors.js';
import { api, ep } from '../lib/api.js';

// The server's limits (app/modules/projects/limits.py): 1–2048 MB, at most 50
// extensions of letters/digits/_ up to 10 characters, 500 characters joined.
const SIZE_MIN = 1;
const SIZE_MAX = 2048;
const TYPES_MAX = 50;
const TYPES_CHARS = 500;
const EXTENSION = /^[a-z0-9_]{1,10}$/;

function uploadProblems(draft, t) {
  const out = {};
  const size = Number(draft.max_file_size_mb);
  if (draft.max_file_size_mb === '' || !Number.isInteger(size) || size < SIZE_MIN || size > SIZE_MAX) {
    out.max_file_size_mb = t('settings.general.sizeRange');
  }
  const types = draft.allowed_file_types || [];
  if (types.length > TYPES_MAX) out.allowed_file_types = t('errors:fields.tooMany', { max: TYPES_MAX });
  else if (types.join(',').length > TYPES_CHARS) out.allowed_file_types = t('errors:fields.tooLong', { max: TYPES_CHARS });
  return out;
}

function GeneralSection({ projectId }) {
  const { t } = useTranslation('console');
  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState(null);
  const [extInput, setExtInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [typeError, setTypeError] = useState(null);
  const [serverErrors, setServerErrors] = useState({});

  useEffect(() => {
    let cancelled = false;
    api.get(ep.settings(projectId)).then((res) => {
      if (cancelled) return;
      if (res.success) {
        setSaved(res.response);
        setDraft({ ...res.response });
      } else {
        setError(res.message);
      }
    });
    return () => { cancelled = true; };
  }, [projectId]);

  const dirty = saved && draft && (
    Number(draft.max_file_size_mb) !== saved.max_file_size_mb
    || draft.allowed_file_types.join(',') !== saved.allowed_file_types.join(',')
  );

  const addExtensions = (raw) => {
    const parts = raw.split(/[,\s]+/).map((p) => p.trim().toLowerCase().replace(/^\./, '')).filter(Boolean);
    if (parts.length === 0) return;
    // Keep what isn't an extension in the box, with a word on why.
    const bad = parts.filter((p) => !EXTENSION.test(p));
    const good = parts.filter((p) => EXTENSION.test(p));
    setDraft((prev) => ({ ...prev, allowed_file_types: Array.from(new Set([...prev.allowed_file_types, ...good])) }));
    setExtInput(bad.join(', '));
    setTypeError(bad.length ? t('settings.general.badType', { ext: bad.join(', ') }) : null);
    setServerErrors({});
  };
  const removeExtension = (ext) => setDraft((prev) => ({ ...prev, allowed_file_types: prev.allowed_file_types.filter((e) => e !== ext) }));

  const problems = draft ? uploadProblems(draft, t) : {};
  const errors = { ...problems, ...serverErrors };

  const save = async () => {
    if (Object.keys(problems).length) return;
    const size = Number(draft.max_file_size_mb);
    setSaving(true);
    const res = await api.put(ep.settings(projectId), {
      data: { max_file_size_mb: size, allowed_file_types: draft.allowed_file_types },
    });
    setSaving(false);
    if (res.success) {
      setSaved(res.response);
      setDraft({ ...res.response });
      toast.success(t('settings.general.saved'));
    } else {
      const placed = fieldErrors(res);
      setServerErrors(placed);
      if (!placed.max_file_size_mb && !placed.allowed_file_types) toast.error(res.message);
    }
  };

  if (error) return <p className="text-sm text-red-600 dark:text-red-400">{error}</p>;
  if (!draft) {
    return (
      <div className="flex items-center justify-center py-10 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  return (
    <section className="space-y-5" data-testid="settings-general">
      <div>
        <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
          <Settings2 className="h-5 w-5 text-muted-foreground" /> {t('settings.general.title')}
        </h2>
        <p className="text-sm text-muted-foreground">{t('settings.general.description')}</p>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="max-file-size">{t('settings.general.maxFileSize')}</Label>
          <Input
            id="max-file-size"
            type="number"
            min={SIZE_MIN}
            max={SIZE_MAX}
            step={1}
            required
            value={draft.max_file_size_mb}
            aria-invalid={errors.max_file_size_mb ? true : undefined}
            aria-describedby={errors.max_file_size_mb ? 'max-file-size-error' : undefined}
            onChange={(e) => { setDraft({ ...draft, max_file_size_mb: e.target.value }); setServerErrors({}); }}
          />
          <FieldError id="max-file-size-error" message={errors.max_file_size_mb} />
          <p className="text-xs text-muted-foreground">{t('settings.general.maxFileSizeHint')}</p>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="allowed-types">{t('settings.general.allowedTypes')}</Label>
        <div className="flex flex-wrap items-center gap-1.5 rounded-md border bg-card p-2">
          {draft.allowed_file_types.map((ext) => (
            <span key={ext} className="inline-flex items-center gap-1 rounded-full border bg-muted/50 px-2 py-0.5 text-xs font-mono">
              .{ext}
              <button type="button" onClick={() => removeExtension(ext)} className="text-muted-foreground hover:text-foreground" aria-label={t('settings.general.removeType', { ext })}>
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          <input
            id="allowed-types"
            value={extInput}
            maxLength={TYPES_CHARS}
            aria-invalid={typeError || errors.allowed_file_types ? true : undefined}
            aria-describedby="allowed-types-error"
            onChange={(e) => { setExtInput(e.target.value); setTypeError(null); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ',' || e.key === ' ') { e.preventDefault(); addExtensions(extInput); }
              if (e.key === 'Backspace' && !extInput && draft.allowed_file_types.length) removeExtension(draft.allowed_file_types[draft.allowed_file_types.length - 1]);
            }}
            onBlur={() => addExtensions(extInput)}
            placeholder={draft.allowed_file_types.length ? '' : t('settings.general.allowedTypesPlaceholder')}
            className="min-w-32 flex-1 bg-transparent text-sm outline-none"
          />
        </div>
        <FieldError id="allowed-types-error" message={typeError || errors.allowed_file_types} />
        <p className="text-xs text-muted-foreground">{t('settings.general.allowedTypesHint')}</p>
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving || !dirty || Object.keys(problems).length > 0} className="gap-2" data-testid="settings-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {t('settings.general.save')}
        </Button>
      </div>
    </section>
  );
}

export const SettingsTab = ({ projectId }) => (
  <div className="space-y-10 max-w-3xl">
    <GeneralSection projectId={projectId} />
  </div>
);
